import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { IconButton, Button, Icon, CircularProgress, Box, Typography } from "@material-ui/core";
import dateFormat from "dateformat";
import ListAltIcon from "@material-ui/icons/ListAlt";
import ClearIcon from "@material-ui/icons/Clear";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import axios from "axios";
import { useState, useEffect, useCallback } from "react";

const columns = [
  {
    field: "bank",
    headerName: "ბანკი",
    flex: 1,
    valueGetter: (params) => {
      return params.row.loan_information.bank.name;
    },
    headerAlign: "center",
    align: "center",
  },
  {
    field: "createdAt",
    headerName: "ატვირთვის თარიღი",
    type: "date",
    flex: 1,
    valueGetter: (params) => {
      return dateFormat(params.row.createdAt, "yyyy-mm-dd");
    },
    headerAlign: "center",
    align: "center",
  },
  {
    field: "lastTrancheDate",
    headerName: "ბოლო ტრანშის თარიღი",
    valueGetter: (params) => {
      let trancheRows = params.row.rows.filter((row) => row.bank_data.tranche_amount > 0);
      if (trancheRows.length === 0) return "ტრანში ვერ მოიძებნა";
      let lastTrancheRow = trancheRows[trancheRows.length - 1];
      return dateFormat(lastTrancheRow.bank_data.payment_date, "yyyy-mm-dd", true);
    },
    type: "date",
    flex: 2,
    headerAlign: "center",
    align: "center",
  },
  //Create column for requested dates (row.loan_information.purpose_dates)
  {
    field: "requested_dates",
    headerName: "მოთხოვნილი თარიღები",
    description: "მოთხოვნილი თარიღები.",
    sortable: true,
    filterable: true,
    flex: 3,
    valueGetter: (params) => {
      let purposeDates = params.row.loan_information.purpose_dates;
      let purposeDatesString = "";
      if (purposeDates && purposeDates.length > 0) {
        purposeDates.forEach((date) => {
          purposeDatesString += dateFormat(date, "yyyy-mm-dd", true) + ", ";
        });
        purposeDatesString = purposeDatesString.slice(0, -2);
      }
      return purposeDatesString;
    },
    renderCell: (params) => {
      let purposeDates = params.row.loan_information.purpose_dates;
      if (!purposeDates || purposeDates.length === 0) return "";

      // Format all dates
      let allDatesFormatted = purposeDates.map((date) => dateFormat(date, "yyyy-mm-dd", true)).join(", ");

      return (
        <div
          style={{
            overflow: "hidden",
            whiteSpace: "normal",
            width: "100%",
          }}
        >
          {allDatesFormatted}
        </div>
      );
    },
  },
  {
    field: "file_path",
    headerName: "გრაფიკის ბმული",
    description: "ბმული.",
    sortable: false,
    flex: 2,
    renderCell: (params) => (
      <a
        variant="contained"
        color="primary"
        size="small"
        style={{
          marginLeft: 16,
          display: "flex",
          alignItems: "center",
          maxWidth: 250,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        href="/"
        onClick={(event) => onCellClick(event, params.row._id)}
      >
        <ListAltIcon />
        {params.row.file_path.split("/")[1]}
      </a>
    ),
    headerAlign: "center",
    align: "center",
  },
  JSON.parse(localStorage.getItem("permissions"))?.includes("MODIFY_SCHEDULING") &&
    localStorage.getItem("role") === "ROLE_AGENCY" && {
      field: "delete_button",
      headerName: (
        <Button onClick={(event) => deleteAllSchedules(event)} startIcon={<ClearAllIcon />} color="secondary" variant="outlined" size="small">
          ყველა გრაფიკის წაშლა
        </Button>
      ),
      description: "ყველა გრაფიკის წაშლა.",
      sortable: false,
      flex: 2,
      renderCell: (params) => (
        <IconButton
          variant="contained"
          size="small"
          color="secondary"
          style={{ marginLeft: 16, display: "flex", alignItems: "center" }}
          href="/"
          onClick={(event) => deleteSchedule(event, params.row._id)}
        >
          <ClearIcon />
        </IconButton>
      ),
      headerAlign: "center",
      align: "center",
    },
];

if (process.env.REACT_APP_ENVIRONMENT === "development") {
  columns.unshift({ field: "_id", headerName: "ID", flex: 1, headerAlign: "center", align: "center" });
}

function deleteSchedule(event, schedule_id) {
  event.preventDefault();
  if (!window.confirm("გსურთ გრაფიკის წაშლა?")) return;

  let component = window.location.href.split("/")[3];
  axios
    .delete(`/agency/delete_schedule/${component}/${schedule_id}`)
    .then((res) => {
      alert(res.data.message);
      window.location.reload();
    })
    .catch((err) => {
      alert(err.response.data.message || "შეცდომა");
    });
}

function deleteAllSchedules(event) {
  event.preventDefault();
  if (!window.confirm("გსურთ ყველა გრაფიკის წაშლა?")) return;

  let component = window.location.href.split("/")[3];
  let loanID = window.location.href.split("/")[4];
  // same endpoint, however, /agency/delete_schedule/:component/:schedule_id/:application_id
  axios
    .delete(`/agency/delete_schedule/${component}/all/${loanID}`)
    .then((res) => {
      alert(res.data.message);
      window.location.reload();
    })
    .catch((err) => {
      alert(err.response.data.message || "შეცდომა");
    });
}

function onCellClick(e, schedule_id) {
  let application_type = window.location.href.split("/")[3];
  e.preventDefault();
  axios
    .post("/view_schedule", { schedule_id, application_type })
    .then((res) => {
      window.location.assign(`${process.env.REACT_APP_BASE_URL}public/schedules/${res.data.scheduleFilePath}`);
    })
    .catch((err) => {
      if (err.response) return alert(err.response.data.message);
      alert("შეცდომა");
    });
}

export default function ScheduleTable({ applicationId, applicationType, canModifyScheduling }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [pageSize, setPageSize] = useState(25);

  const fetchSchedules = useCallback(async (page = 1, limit = pageSize) => {
    if (!applicationId || !applicationType) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/application/${applicationType}/${applicationId}/schedules?page=${page}&limit=${limit}`);
      setSchedules(response.data.schedules);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching schedules:", err);
      setError(err.response?.data?.message || "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  }, [applicationId, applicationType, pageSize]);

  useEffect(() => {
    fetchSchedules(1, pageSize);
  }, [fetchSchedules, pageSize]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400, width: "100%" }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>გრაფიკები იტვირთება...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400, width: "100%" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!schedules || schedules.length === 0) {
    return null;
  }

  return (
    <div style={{ height: 400, width: "100%" }}>
      <DataGrid
        getRowId={(row) => row._id}
        rows={schedules}
        canModifyScheduling={canModifyScheduling}
        columns={columns}
        pageSize={pageSize}
        pagination
        paginationMode="server"
        rowCount={pagination.total}
        page={pagination.page - 1}
        onPageChange={(newPage) => fetchSchedules(newPage + 1, pageSize)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          fetchSchedules(1, newPageSize);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        disableColumnMenu={false}
        components={{
          Toolbar: GridToolbar,
        }}
        componentsProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
      />
    </div>
  );
}
